// CreateSalesForm.jsx (CREATE ONLY)
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Plus, Trash2, UploadCloud, FileText, Image as ImageIcon, Equal, Percent, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import { BASE_URL } from "shared/lib/config";

/* =========================================================
 * Auth + fetch helpers (clients only)
 * ========================================================= */
const getStore = () => {
  if (sessionStorage.getItem("idToken")) return sessionStorage;
  if (localStorage.getItem("idToken")) return localStorage;
  return localStorage;
};

const autoGrowTextarea = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

const blockNumberArrows = (e) => {
  if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
};


const getToken = () => getStore().getItem("idToken");

async function fetchJson(url, opts = {}) {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    ...(opts.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, { ...opts, headers });
  const contentType = res.headers.get("content-type") || "";

  let data = null;
  if (contentType.includes("application/json")) data = await res.json().catch(() => null);
  else data = await res.text().catch(() => null);

  if (!res.ok) {
    const msg = data?.message || (typeof data === "string" && data) || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

 return data;
}

/* =========================================================
 * Helpers
 * ========================================================= */
const toDateInput = (v) => {
  if (!v) return "";
  try {
    return new Date(v).toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(Number(amount || 0));

const formatDateId = (d) => {
  try {
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return "-";
    return dt.toLocaleDateString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "-";
  }
};

const generateLineId = () => `line_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const clampPercent = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(100, x));
};

const clampNonNegNumber = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, x);
};

const clampIntRange = (n, min, max) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return min;
  const v = Math.floor(x);
  return Math.max(min, Math.min(max, v));
};

const clampTermMonths = (n) => clampIntRange(n, 1, 12);

const computeTotals = ({ items, ppn, pph, discount }) => {
  const subtotal = (items || []).reduce((sum, it) => sum + (Number(it.amount) || 0), 0);

  const discPct = clampPercent(discount);
  const ppnPct = clampPercent(ppn);
  const pphPct = clampPercent(pph);

  const total_discount = (subtotal * discPct) / 100;
  const afterDiscount = Math.max(0, subtotal - total_discount);

  const total_ppn = (afterDiscount * ppnPct) / 100;
  const total_pph = (afterDiscount * pphPct) / 100;

  const grand_total = Math.max(0, afterDiscount + total_ppn - total_pph);
  return { subtotal, total_discount, total_ppn, total_pph, grand_total };
};

function prettyFileSize(bytes) {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let u = 0;
  let v = n;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v.toFixed(u === 0 ? 0 : 1)} ${units[u]}`;
}

function isImageMime(mime) {
  return String(mime || "").startsWith("image/");
}
function isPdfMime(mime) {
  return String(mime || "") === "application/pdf";
}

function normalizeMode(v) {
  const x = String(v || "").toLowerCase().trim();
  return x === "percentage" ? "percentage" : "equal";
}

function getInvoiceCount(termOfPayment) {
  if (termOfPayment === "" || termOfPayment === null || termOfPayment === undefined) return 1;
  const n = clampTermMonths(termOfPayment);
  return Math.max(1, n);
}


function buildDefaultPercentages(n) {
  if (n <= 1) return [100];
  const base = Math.floor(100 / n);
  const rem = 100 - base * n;
  const arr = Array.from({ length: n }, () => base);
  arr[arr.length - 1] += rem;
  return arr;
}

function sumNums(arr) {
  return (arr || []).reduce((s, x) => s + (Number(x) || 0), 0);
}

function coercePercentArray(arr, n) {
  const out = Array.isArray(arr) ? arr.slice(0, n) : [];
  while (out.length < n) out.push(""); // allow empty display

  return out.map((x) => {
    if (x === "") return "";           // keep empty string
    return clampNonNegNumber(x);       // otherwise numeric clamp
  });
}


function addMonthsKeepDay(dateLike, monthsToAdd) {
  const d0 = dateLike instanceof Date ? new Date(dateLike) : new Date(dateLike);
  if (Number.isNaN(d0.getTime())) return new Date();
  const d = new Date(d0);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + Number(monthsToAdd || 0));
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return d;
}

function splitAmountByPercentages(grandTotal, percentages) {
  const total = Math.max(0, Number(grandTotal) || 0);
  const p = Array.isArray(percentages) ? percentages : [100];
  if (!p.length) return [total];

  const raw = p.map((pct) => (total * (Number(pct) || 0)) / 100);
  const rounded = raw.map((x) => Math.floor(x));
  const sumRounded = rounded.reduce((s, x) => s + x, 0);
  const diff = total - sumRounded;
  rounded[rounded.length - 1] = Math.max(0, rounded[rounded.length - 1] + diff);

  return rounded;
}

const PROJECT_CATEGORIES = ["Deus Enhance", "Deus Discover", "Potentia"];

function normalizeProjectCategory(v) {
  const s = String(v ?? "")
    .replace(/\s+/g, " ") // collapse weird whitespace
    .trim()
    .toLowerCase();

  if (s === "deus enhance") return "Deus Enhance";
  if (s === "deus discover") return "Deus Discover";
  if (s === "potentia") return "Potentia";
  return "Potentia";
}


/* =========================================================
 * Confirm Modal
 * ========================================================= */
function ConfirmTextModal({ open, onClose, onConfirm, submitting }) {
  const REQUIRED = "Saya yakin ini benar";
  const [text, setText] = useState("");

  useEffect(() => {
    if (!open) return;
    setText("");
  }, [open]);

  if (!open) return null;

  const normalize = (s) =>
  String(s || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const ok = normalize(text) === normalize(REQUIRED);


  return createPortal(
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="absolute inset-0 bg-black/50" />

      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-semibold">Konfirmasi Data</span>
            </div>
            <div className="mt-1 text-base font-bold text-slate-900">Pastikan Sales Order sudah benar</div>
            <p className="mt-1 text-sm text-slate-600">
              Untuk melanjutkan, ketik tepat: <b>{REQUIRED}</b>
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className={[
              "w-9 h-9 inline-flex items-center justify-center rounded-xl border border-slate-200",
              submitting ? "text-slate-300 cursor-not-allowed" : "text-slate-500 hover:bg-slate-50",
            ].join(" ")}
            title="Tutup"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={submitting}
            placeholder='Ketik: "Saya yakin ini benar"'
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
          />

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={[
                "px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold",
                submitting ? "text-slate-400 bg-slate-50 cursor-not-allowed" : "text-slate-700 hover:bg-slate-50",
              ].join(" ")}
            >
              Batal
            </button>

            <button
              type="button"
              onClick={() => onConfirm?.()}
              disabled={!ok || submitting}
              className={[
                "px-4 py-2 rounded-xl text-sm font-semibold text-white",
                !ok || submitting ? "bg-amber-300 cursor-not-allowed" : "bg-amber-600 hover:bg-amber-700",
              ].join(" ")}
            >
              {submitting ? "Memproses..." : "Ya, Simpan"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
function normalizePercForDuplicate(raw, nInv) {
  console.log("🔵 normalize input:", raw, "nInv:", nInv);

  let arr = Array.isArray(raw) ? raw.slice(0, nInv) : [];
  while (arr.length < nInv) arr.push("");

  arr = arr.map((x) => (x === "" ? "" : Number(x)));

  const nums = arr.map((x) => (x === "" ? 0 : Number(x) || 0));
  const total = nums.reduce((s, v) => s + v, 0);

  if (nInv >= 2 && total > 0 && total !== 100) {
    const diff = 100 - total;
    const last = arr[nInv - 1] === "" ? 0 : Number(arr[nInv - 1]) || 0;
    arr[nInv - 1] = last + diff;
  }

  console.log("🔵 normalize output:", arr);
  return arr;
}



/* =========================================================
 * Component (CREATE ONLY)
 * ========================================================= */
export default function SelectInvoiceForm({ isOpen, onClose, onSubmit, initialData }) {
  function mapInitialToForm(initial) {
  const top = getInvoiceCount(initial?.term_of_payment ?? 1);

const hasPerc = top >= 2 && Array.isArray(initial?.installment_percentages) && initial.installment_percentages.length > 0;

const perc = hasPerc
  ? normalizePercForDuplicate(initial.installment_percentages, top) // ✅ will pad + fix remainder to 100
  : [];


  return {
    project_category: normalizeProjectCategory(initial?.project_category ?? "Potentia"),
    reference_document_type: initial?.reference_document_type ?? "PO",
    term_of_payment: String(initial?.term_of_payment ?? 1),

    payment_plan_mode: perc.length ? "percentage" : "equal",
    installment_percentages: perc,

    client_id: initial?.client_id?._id ?? initial?.client_id ?? "",
    creation_date: toDateInput(initial?.creation_date ?? new Date()),
    ppn: Number(initial?.ppn ?? 11),
    pph: Number(initial?.pph ?? 0),
    discount: Number(initial?.discount ?? 0),

    items: (initial?.items?.length ? initial.items : []).map((it) => ({
      id: generateLineId(),
      name: it?.name ?? "",
      description: it?.description ?? "",
      qty: String(it?.qty ?? ""),
      price: String(it?.price ?? ""),
      amount: Number(it?.amount) || 0,
    })).length
      ? (initial?.items || []).map((it) => ({
          id: generateLineId(),
          name: it?.name ?? "",
          description: it?.description ?? "",
          qty: String(it?.qty ?? ""),
          price: String(it?.price ?? ""),
          amount: Number(it?.amount) || 0,
        }))
      : [{ id: generateLineId(), name: "", description: "", qty: "", price: "", amount: 0 }],
  };
}




  const [loadingClients, setLoadingClients] = useState(false);
  const [clients, setClients] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Confirmation modal
  const [confirmOpen, setConfirmOpen] = useState(false);

  // File state (no direct upload here anymore)
  const [localFile, setLocalFile] = useState(null);

  // Cache payload to submit after confirm
  const pendingRef = useRef({ payload: null, file: null });
  const initialPercRef = useRef([]);

  const bodyRef = useRef(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState(() => ({
    project_category: "Potentia",
    reference_document_type: "PO",
    term_of_payment: "1",

    payment_plan_mode: "equal",
    installment_percentages: [],
    client_id: "",
    creation_date: toDateInput(new Date()),
    ppn: 11,
    pph: 0,
    discount: 0,
    items: [{ id: generateLineId(), name: "", description: "", qty: "", price: "", amount: 0 }],

  }));

 useEffect(() => {
  if (!isOpen) return;

  console.log("🟡 initialData (raw from parent):", initialData);
  console.log("🟡 initialData.installment_percentages:", initialData?.installment_percentages);

  const base = {
    project_category: "Potentia",
    reference_document_type: "PO",
    term_of_payment: "1",
    payment_plan_mode: "equal",
    installment_percentages: [],
    client_id: "",
    creation_date: toDateInput(new Date()),
    ppn: 11,
    pph: 0,
    discount: 0,
    items: [{ id: generateLineId(), name: "", description: "", qty: "", price: "", amount: 0 }],
  };

  const mapped = initialData ? mapInitialToForm(initialData) : base;
  // keep the original percentages (for Reset to restore exact value incl decimals)
initialPercRef.current = Array.isArray(mapped.installment_percentages)
  ? mapped.installment_percentages
  : [];

  console.log("🟢 mapped formData:", mapped);
  console.log("🟢 mapped.installment_percentages:", mapped.installment_percentages);

  setFormData(mapped);

  setConfirmOpen(false);
  pendingRef.current = { payload: null, file: null };

  setLocalFile(null);
  if (fileInputRef.current) fileInputRef.current.value = "";

  requestAnimationFrame(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  });
}, [isOpen, initialData]);


  // lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // fetch clients for dropdown
  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      setLoadingClients(true);
      try {
        const data = await fetchJson(`${BASE_URL}/api/invoice-tracker/clients?limit=500`);
        setClients(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        toast.error(err?.message || "Gagal memuat data client");
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    };

    load();
  }, [isOpen]);

  // Keep installment_percentages length in sync when term_of_payment changes (only in percentage mode)
  useEffect(() => {
    if (!isOpen) return;

    const nInv = getInvoiceCount(formData.term_of_payment);

    setFormData((prev) => {
      if (normalizeMode(prev.payment_plan_mode) !== "percentage") return prev;

      const next =
  prev.installment_percentages.length < nInv
    ? normalizePercForDuplicate(prev.installment_percentages, nInv)
    : prev.installment_percentages;

      const same =
        Array.isArray(prev.installment_percentages) &&
        prev.installment_percentages.length === next.length &&
       prev.installment_percentages.every((v, i) => v === next[i]);


      if (same) return prev;
      return { ...prev, installment_percentages: next };
    });
  }, [formData.term_of_payment, formData.payment_plan_mode, isOpen]);

  const totals = useMemo(() => {
    const cleanedItems = (formData.items || []).map((it) => {
      const qty = Math.max(1, Number(it.qty) || 1);
      const price = Math.max(0, Number(it.price) || 0);
      const amount = Math.max(0, qty * price);
      return { ...it, qty, price, amount };
    });

    return computeTotals({
      items: cleanedItems,
      ppn: formData.ppn,
      pph: formData.pph,
      discount: formData.discount,
    });
  }, [formData.items, formData.ppn, formData.pph, formData.discount]);

  // Preview computed from CURRENT form totals
  const invoiceCount = getInvoiceCount(formData.term_of_payment);
  const planMode = normalizeMode(formData.payment_plan_mode);
  const showPlan = invoiceCount >= 2;

  const planPerc = useMemo(() => {
  if (invoiceCount <= 1) return [100];
  if (planMode === "percentage") {
    const raw = coercePercentArray(formData.installment_percentages, invoiceCount);
    // numeric for calculations ("" => 0)
    return raw.map((v) => (v === "" ? 0 : Number(v) || 0));
  }
  return buildDefaultPercentages(invoiceCount);
}, [formData.installment_percentages, invoiceCount, planMode]);


  const planTotal = useMemo(() => sumNums(planPerc), [planPerc]);

  const previewRows = useMemo(() => {
    const baseDate = formData.creation_date ? new Date(formData.creation_date) : new Date();
    const amounts = splitAmountByPercentages(Math.round(totals.grand_total), planPerc);

    return Array.from({ length: Math.max(1, invoiceCount) }).map((_, idx) => {
      const due = addMonthsKeepDay(baseDate, idx);
      return { idx, pct: Number(planPerc[idx] ?? 0) || 0, amount: Number(amounts[idx] ?? 0) || 0, dueDate: due };
    });
  }, [formData.creation_date, invoiceCount, planPerc, totals.grand_total]);

  if (!isOpen) return null;

const updateItem = (id, field, value) => {
  setFormData((prev) => ({
    ...prev,
    items: (prev.items || []).map((it) => {
      if (it.id !== id) return it;

      const next = { ...it, [field]: value };

      // compute using fallbacks, but DO NOT overwrite the raw strings in state
      const qtyNum = Math.max(1, Number(next.qty) || 1);
      const priceNum = Math.max(0, Number(next.price) || 0);
      next.amount = Math.max(0, qtyNum * priceNum);

      return next;
    }),
  }));
};

 const addItem = () => {
  setFormData((prev) => ({
    ...prev,
    items: [
      ...(prev.items || []),
      { id: generateLineId(), name: "", description: "", qty: "", price: "", amount: 0 },
    ],
  }));
};


  const removeItem = (id) => {
    setFormData((prev) => {
      const next = (prev.items || []).filter((it) => it.id !== id);
      return { ...prev, items: next.length ? next : prev.items };
    });
  };

function normalizeTo100KeepEditedForwardOnly(arr, editedIdx) {
  const n = arr.length || 1;
  if (n <= 1) return [100];

  // clamp edited
  const edited = clampPercent(arr[editedIdx] ?? 0);

  // ONLY adjust slots AFTER editedIdx
  const forward = [];
  for (let i = editedIdx + 1; i < n; i++) forward.push(i);

  // if there's no slot after it, just return with edited clamped
  if (forward.length === 0) {
    const out = arr.map((x) => clampPercent(x ?? 0));
    out[editedIdx] = edited;
    // drift fix: force total 100 by adjusting edited itself
    const total = out.reduce((s, v) => s + v, 0);
    const diff = 100 - total;
    out[editedIdx] = clampPercent(out[editedIdx] + diff);
    return out;
  }

  let remaining = 100 - edited;
  if (remaining < 0) remaining = 0;

  // distribute remaining equally to forward slots
  const base = Math.floor(remaining / forward.length);
  let rem = remaining - base * forward.length;

  const out = arr.map((x) => clampPercent(x ?? 0));
  out[editedIdx] = edited;

  for (const i of forward) {
    out[i] = base + (rem > 0 ? 1 : 0);
    if (rem > 0) rem--;
  }

  // IMPORTANT: do NOT change earlier slots (0..editedIdx-1)

  // drift fix: ensure total exactly 100 by adjusting the LAST forward slot
  const total = out.reduce((s, v) => s + v, 0);
  const diff = 100 - total;
  if (diff !== 0) {
    const lastForwardIdx = forward[forward.length - 1];
    out[lastForwardIdx] = clampPercent(out[lastForwardIdx] + diff);
  }

  return out;
}

const updatePercentageAt = (idx, value) => {
  setFormData((prev) => {
    const nInv = getInvoiceCount(prev.term_of_payment);
    let next = coercePercentArray(prev.installment_percentages, nInv);

   // allow empty while typing
if (value === "") {
  next[idx] = "";
  return { ...prev, installment_percentages: next };
}

// commit numeric value
const v = clampPercent(value);
next[idx] = v;

if (nInv >= 2) {
  const numeric = next.map((x) => (x === "" ? 0 : Number(x) || 0));
  next = normalizeTo100KeepEditedForwardOnly(numeric, idx);

}

return { ...prev, installment_percentages: next };


    return { ...prev, installment_percentages: next };
  });
};





  const setMode = (mode) => {
    const m = normalizeMode(mode);
    setFormData((prev) => {
      if (m === "percentage") {
        const nInv = getInvoiceCount(prev.term_of_payment);
      const existing = coercePercentArray(prev.installment_percentages, nInv);
const hasSome = existing.some((x) => Number(x) > 0);

let perc = hasSome ? existing : buildDefaultPercentages(nInv);

// normalize to 100 if needed
const nums = perc.map((x) => (x === "" ? 0 : Number(x) || 0));
const total = nums.reduce((s, v) => s + v, 0);
if (nInv >= 2 && total !== 100 && total > 0) {
  const diff = 100 - total;
  nums[nInv - 1] = (nums[nInv - 1] || 0) + diff;
  perc = nums;
}

return {
  ...prev,
  payment_plan_mode: "percentage",
  installment_percentages: perc,
};

      }
      return { ...prev, payment_plan_mode: "equal", installment_percentages: [] };
    });
  };

  const resetPercentagesToDefault = () => {
  setFormData((prev) => {
    const nInv = getInvoiceCount(prev.term_of_payment);

    // restore original duplicated values (including decimals)
    const original = coercePercentArray(initialPercRef.current, nInv);
    const hasOriginal = original.some((x) => x !== "" && Number(x) > 0);

    return {
      ...prev,
      installment_percentages: hasOriginal ? original : buildDefaultPercentages(nInv),
    };
  });
};

  const validate = () => {
    const missing = [];

    if (!String(formData.client_id || "").trim()) missing.push("Mitra (Client)");
    if (!normalizeProjectCategory(formData.project_category)) missing.push("Project Category");

    const creationDate = new Date(formData.creation_date);
    if (!formData.creation_date || Number.isNaN(creationDate.getTime())) missing.push("Tanggal Pembuatan");

    const topMonths = Number(formData.term_of_payment);
    if (!Number.isFinite(topMonths) || topMonths < 1 || topMonths > 12) {
      missing.push("Term of Payment harus 1 - 12 bulan");
    }

    const items = formData.items || [];
    const validItems = items.filter((it) => String(it.name || "").trim());
    if (!validItems.length) missing.push("Minimal 1 item dengan nama item");

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!String(it.name || "").trim()) continue;
      if ((Number(it.qty) || 0) < 1) missing.push(`Item #${i + 1}: qty minimal 1`);
      if ((Number(it.price) || 0) < 0) missing.push(`Item #${i + 1}: price minimal 0`);
    }

    const ppn = Number(formData.ppn);
    const pph = Number(formData.pph);
    const disc = Number(formData.discount);

    if (Number.isNaN(ppn) || ppn < 0 || ppn > 100) missing.push("PPN harus 0-100");
    if (Number.isNaN(pph) || pph < 0 || pph > 100) missing.push("PPH harus 0-100");
    if (Number.isNaN(disc) || disc < 0 || disc > 100) missing.push("Discount harus 0-100");

    const mode = normalizeMode(formData.payment_plan_mode);
    const nInv = getInvoiceCount(formData.term_of_payment);
    if (mode === "percentage" && nInv >= 2) {
      const arr = coercePercentArray(formData.installment_percentages, nInv);
      const total = sumNums(arr);
      if (total !== 100) missing.push(`Total persentase payment plan harus 100 (sekarang ${total})`);
    }

// ✅ file OPTIONAL (PDF only if provided)
if (localFile) {
  const isPdf = isPdfMime(localFile.type) || String(localFile.name || "").toLowerCase().endsWith(".pdf");
  if (!isPdf) missing.push("Reference Document harus PDF");
}


    if (missing.length) {
      toast.error(`Lengkapi / perbaiki:\n- ${missing.join("\n- ")}`);
      return false;
    }
    return true;
  };

  const buildPayload = () => {
    const cleanedItems = (formData.items || [])
      .filter((it) => String(it.name || "").trim())
      .map((it) => {
        const qty = Math.max(1, Number(it.qty) || 1);
        const price = Math.max(0, Number(it.price) || 0);
        const amount = Math.max(0, qty * price);

        return {
          name: String(it.name || "").trim(),
          description: String(it.description || "").trim(),
          qty,
          price,
          amount,
        };
      });

    const computed = computeTotals({
      items: cleanedItems,
      ppn: formData.ppn,
      pph: formData.pph,
      discount: formData.discount,
    });

    const mode = normalizeMode(formData.payment_plan_mode);
    const nInv = getInvoiceCount(formData.term_of_payment);

   const installment_percentages =
  nInv >= 2
    ? (mode === "percentage"
        ? coercePercentArray(formData.installment_percentages, nInv)
        : buildDefaultPercentages(nInv)) // ✅ send 33/33/34 for equal mode too
    : [];


    return {
      reference_document_type: formData.reference_document_type,
      project_category: normalizeProjectCategory(formData.project_category),
      term_of_payment: clampTermMonths(formData.term_of_payment),
      installment_percentages,
      client_id: formData.client_id,
      creation_date: new Date(formData.creation_date).toISOString(),
      items: cleanedItems,
      ppn: clampPercent(formData.ppn),
      pph: clampPercent(formData.pph),
      discount: clampPercent(formData.discount),
      subtotal: computed.subtotal,
      total_discount: computed.total_discount,
      total_ppn: computed.total_ppn,
      total_pph: computed.total_pph,
      grand_total: computed.grand_total,
    };
  };

  const handlePickFile = (file) => {
    if (!file) return;

    const isPdf = isPdfMime(file.type) || String(file.name || "").toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Untuk proof sales, hanya PDF yang didukung.");
      return;
    }

    setLocalFile(file);
  };

  const clearFile = () => {
    setLocalFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const fileBadge = (() => {
    if (localFile) {
      const mime = localFile.type || "application/pdf";
      const icon = isPdfMime(mime) ? (
        <FileText className="w-4 h-4" />
      ) : isImageMime(mime) ? (
        <ImageIcon className="w-4 h-4" />
      ) : (
        <UploadCloud className="w-4 h-4" />
      );

      return (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-2 text-slate-800">
            {icon}
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{localFile.name}</div>
              <div className="text-xs text-slate-600 truncate">
                {mime || "unknown"} • {prettyFileSize(localFile.size)}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">File akan diupload setelah Sales Order tersimpan (signed URL + commit).</div>
            </div>
          </div>

          <button
            type="button"
            onClick={clearFile}
            className="text-xs font-semibold text-slate-700 hover:text-slate-900 underline"
            disabled={submitting}
            title="Batalkan file terpilih"
          >
            Batal
          </button>
        </div>
      );
    }

    return null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    // Open confirmation modal first
    const payload = buildPayload();
    pendingRef.current = { payload, file: localFile || null };
    setConfirmOpen(true);
  };

  const confirmAndSubmit = async () => {
    const pending = pendingRef.current;
    if (!pending?.payload) {
      toast.error("Payload tidak ditemukan");
      setConfirmOpen(false);
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit?.({ payload: pending.payload, file: pending.file });
      // parent will close modal, but we can also close here safely:
      setConfirmOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Gagal menyimpan Sales Order");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !confirmOpen) onClose?.();
      }}
    >
      <ConfirmTextModal
        open={confirmOpen}
        submitting={submitting}
        onClose={() => {
          if (submitting) return;
          setConfirmOpen(false);
        }}
        onConfirm={confirmAndSubmit}
      />

      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl border border-gray-200 flex flex-col max-h-[90vh] overflow-hidden">
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-200">
          <div className="flex items-start justify-between p-6">
            <div>
              <p className="text-sm text-blue-600 mb-1">Sales Order</p>
              <h2 className="text-2xl font-bold text-gray-900">Buat Sales Order</h2>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="ml-4 inline-flex items-center justify-center w-10 h-10 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Tutup"
              title="Tutup"
              disabled={submitting}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
          <div ref={bodyRef} className="p-6 space-y-6 overflow-y-auto min-h-0">
            {/* Client / Meta */}
           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mitra <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.client_id}
                  onChange={(e) => setFormData((p) => ({ ...p, client_id: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loadingClients || submitting}
                >
                  <option value="">{loadingClients ? "Memuat client..." : "Pilih Mitra"}</option>
                  {clients.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.client_name} {c.client_id ? `(${c.client_id})` : ""}
                    </option>
                  ))}
                </select>
              </div>
                  <div>
  <label className="block text-sm font-semibold text-gray-700 mb-2">
    Project Category <span className="text-red-500">*</span>
  </label>

  <select
    value={formData.project_category}
    onChange={(e) => setFormData((p) => ({ ...p, project_category: e.target.value }))}
    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
    disabled={submitting}
  >
    {PROJECT_CATEGORIES.map((c) => (
      <option key={c} value={c}>
        {c}
      </option>
    ))}
  </select>
</div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tanggal Pembuatan <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.creation_date}
                  onChange={(e) => setFormData((p) => ({ ...p, creation_date: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={submitting}
                />
              </div>
            </div>

            {/* Reference Document */}
            <div className="border rounded-2xl border-gray-200 p-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-gray-800">Reference Document</h3>

                <div className="flex items-center gap-2">
                  <label className="text-sm font-semibold text-gray-700">Type</label>
                  <select
                    value={formData.reference_document_type}
                    onChange={(e) => setFormData((p) => ({ ...p, reference_document_type: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={submitting}
                  >
                    <option value="PO">PO</option>
                    <option value="Quotation">Quotation</option>
                    <option value="MOU">MOU</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
        Upload File (PDF only) 
      </label>

                  <div className="flex items-center gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={(e) => handlePickFile(e.target.files?.[0] || null)}
                      className="block w-full text-sm text-gray-700 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      disabled={submitting}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    File akan diupload ke GCS via signed URL setelah Sales Order berhasil dibuat.
                  </p>
                </div>

                <div className="md:col-span-2 space-y-3">
                  {fileBadge}

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Term of Payment (bulan) <span className="text-red-500">*</span>
                    </label>
               <input
                      type="number"
                      min="1"
                      max="12"
                      step="1"
                      value={formData.term_of_payment === "" ? "" : String(formData.term_of_payment)}
                      onChange={(e) => {
                        const v = e.target.value; // "" when cleared
                        setFormData((p) => ({ ...p, term_of_payment: v }));
                      }}
                      onBlur={() => {
                        setFormData((p) => {
                          if (p.term_of_payment === "") return { ...p, term_of_payment: "1" };
                          return { ...p, term_of_payment: String(clampTermMonths(p.term_of_payment)) };
                        });
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={submitting}
                    />


                    <p className="mt-1 text-xs text-gray-500">
                      Akan dibuat <b>{invoiceCount}</b> invoice (rule: max(1, term_of_payment)).
                    </p>
                  </div>

                  {/* Payment Plan */}
                  {showPlan && (
                    <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">Payment Plan (pembagian tagihan)</div>
                          <div className="text-xs text-gray-600">Pilih pembagian untuk {invoiceCount} invoice (total harus 100% bila custom).</div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setMode("equal")}
                            disabled={submitting}
                            className={[
                              "inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold transition-colors",
                              planMode === "equal" ? "bg-white border-blue-200 text-blue-700" : "bg-white border-gray-200 text-gray-700 hover:bg-gray-100",
                            ].join(" ")}
                          >
                            <Equal className="w-4 h-4" />
                            Equal
                          </button>

                          <button
                            type="button"
                            onClick={() => setMode("percentage")}
                            disabled={submitting}
                            className={[
                              "inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold transition-colors",
                              planMode === "percentage"
                                ? "bg-white border-blue-200 text-blue-700"
                                : "bg-white border-gray-200 text-gray-700 hover:bg-gray-100",
                            ].join(" ")}
                          >
                            <Percent className="w-4 h-4" />
                            Custom %
                          </button>
                        </div>
                      </div>

                      {planMode === "equal" ? (
                        <div className="mt-3 text-sm text-gray-700">
                          Pembagian otomatis setara. Contoh 3 bulan → <b>{buildDefaultPercentages(invoiceCount).join(" / ")}%</b>.
                        </div>
                      ) : (
                        <div className="mt-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold text-gray-800">Persentase per invoice</div>

                            <button
                              type="button"
                              onClick={resetPercentagesToDefault}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 text-sm font-semibold"
                              disabled={submitting}
                            >
                              <RotateCcw className="w-4 h-4" />
                              Reset
                            </button>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {Array.from({ length: invoiceCount }).map((_, idx) => (
                              <div key={idx} className="rounded-xl border border-gray-200 bg-white p-3">
                                <div className="text-xs font-semibold text-gray-600 mb-2">Pembayaran #{idx + 1}</div>
                                <div className="flex items-center gap-2">
                              <input
        type="number"
        min="0"
        max="100"
        step="1"
        value={formData.installment_percentages?.[idx] ?? ""}
        onChange={(e) => updatePercentageAt(idx, e.target.value)}
        onBlur={() => {
          setFormData((prev) => {
            const nInv = getInvoiceCount(prev.term_of_payment);
            let next = coercePercentArray(prev.installment_percentages, nInv);

            // finalize empty → 0 on blur
            if (next[idx] === "") next[idx] = 0;

            const numeric = next.map((x) => Number(x) || 0);
            return {
              ...prev,
              installment_percentages: normalizeTo100KeepEditedForwardOnly(numeric, idx),

            };
          });
        }}
        className="no-spinner w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
        disabled={submitting}
      />

                                        <span className="text-sm font-semibold text-gray-700">%</span>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="flex items-center justify-between rounded-xl border px-3 py-2 bg-white">
                            <span className="text-sm text-gray-700">Total</span>
                            <span className={["text-sm font-bold", planTotal === 100 ? "text-emerald-700" : "text-red-600"].join(" ")}>
                              {planTotal}%
                            </span>
                          </div>

                          {planTotal !== 100 && (
                            <div className="text-xs text-red-600">
                              Total harus tepat <b>100%</b> agar bisa disimpan.
                            </div>
                          )}
                        </div>
                      )}

                      {/* Preview */}
                      <div className="mt-5 rounded-2xl border border-gray-200 bg-white overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                          <div className="text-sm font-semibold text-gray-900">Preview Invoice</div>
                          <div className="text-xs text-gray-600">Nominal dihitung dari <b>Grand Total</b> saat ini. Due date per bulan dari tanggal pembuatan.</div>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-white border-b border-gray-200">
                              <tr>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">#</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">%</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Due Date</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {previewRows.map((row) => (
                                <tr key={row.idx} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-700 font-semibold text-center">{row.idx + 1}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700 text-center">{row.pct}%</td>
                                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap text-center">{formatCurrency(row.amount)}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap text-center">{formatDateId(row.dueDate)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                          <span className="text-sm text-gray-600">Grand Total</span>
                          <span className="text-sm font-bold text-gray-900">{formatCurrency(totals.grand_total)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="border rounded-2xl border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Items</h3>
                  <p className="text-xs text-gray-500">Amount otomatis = qty × price (mengikuti schema Sales)</p>
                </div>

                <button
                  type="button"
                  onClick={addItem}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-blue-600 hover:bg-blue-50 transition-colors"
                  disabled={submitting}
                >
                  <Plus className="w-4 h-4" />
                  Tambah Item
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-12">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[220px]">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[320px]">Description</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Qty</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-44">Price</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-44">Amount</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">Aksi</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {(formData.items || []).map((it, idx) => (
                      <tr key={it.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">{idx + 1}</td>

                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={it.name}
                            onChange={(e) => updateItem(it.id, "name", e.target.value)}
                            placeholder="Nama item"
                            className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={submitting}
                          />
                        </td>

                      <td className="px-4 py-3 align-middle">
                        <textarea
                          value={it.description}
                          onChange={(e) => updateItem(it.id, "description", e.target.value)}
                          onInput={(e) => autoGrowTextarea(e.currentTarget)}
                          onFocus={(e) => autoGrowTextarea(e.currentTarget)}
                          placeholder="Deskripsi item"
                          rows={1}
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[42px] overflow-hidden"
                          disabled={submitting}
                        />
                      </td>


                        <td className="px-4 py-3 text-center">
                         <input
                          type="number"
                          min="1"
                          step="1"
                          value={it.qty === "" ? "" : it.qty}
                          placeholder="1"
                          onKeyDown={blockNumberArrows}
                          onChange={(e) => updateItem(it.id, "qty", e.target.value)}
                          className="no-spinner w-24 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                          disabled={submitting}
                        />

                        </td>

                        <td className="px-4 py-3 text-center">
                                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={it.price === "" ? "" : it.price}
                        placeholder="0"
                        onKeyDown={blockNumberArrows}
                        onChange={(e) => updateItem(it.id, "price", e.target.value)}
                        className="no-spinner w-40 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                        disabled={submitting}
                      />

                        </td>

                        <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(it.amount)}</td>

                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(it.id)}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-xl text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                            disabled={(formData.items || []).length <= 1 || submitting}
                            title="Hapus item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>

                  <tfoot>
  <tr className="border-t border-gray-200 bg-white">
    {/* Label column */}
    <td
      colSpan={5}
      className="px-4 py-3 text-right text-sm font-semibold text-gray-600"
    >
      Subtotal
    </td>

    {/* Amount column */}
    <td className="px-4 py-3 text-right text-sm font-bold text-gray-900 whitespace-nowrap">
      {formatCurrency(totals.subtotal)}
    </td>

    {/* Aksi column (empty) */}
    <td className="px-4 py-3" />
  </tr>
</tfoot>

                </table>
              </div>
            </div>

            {/* Totals */}
        {/* Totals */}
<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

  {/* LEFT: PPN/PPH/Discount compact rows */}
  <div className="border rounded-2xl border-gray-200 p-4">
    <h3 className="text-sm font-semibold text-gray-800 mb-3">PPN / PPH / Discount</h3>

    <div className="space-y-3">
      {/* Row */}
      <div className="flex items-center justify-between gap-4">
        <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
          PPN (%) <span className="text-gray-400 font-normal">(default 11)</span>
        </label>
              <input
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={formData.ppn}
          onKeyDown={blockNumberArrows}
          onChange={(e) => setFormData((p) => ({ ...p, ppn: e.target.value }))}
          className="no-spinner w-44 px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-left"
          disabled={submitting}
        />

      </div>

      <div className="flex items-center justify-between gap-4">
        <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">PPH (%)</label>
      <input
        type="number"
        min="0"
        max="100"
        step="0.01"
        value={formData.pph}
        onKeyDown={blockNumberArrows}
        onChange={(e) => setFormData((p) => ({ ...p, pph: e.target.value }))}
        className="no-spinner w-44 px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
        disabled={submitting}
      />

      </div>

      <div className="flex items-center justify-between gap-4">
        <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">Discount (%)</label>
      <input
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={formData.discount}
          onKeyDown={blockNumberArrows}
          onChange={(e) => setFormData((p) => ({ ...p, discount: e.target.value }))}
          className="no-spinner w-44 px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={submitting}
        />

      </div>
    </div>
  </div>

  {/* RIGHT: Totals with Rp + number aligned like columns */}
  <div className="lg:col-span-2 border rounded-2xl border-gray-200 p-4 bg-gray-50">
    <h3 className="text-sm font-semibold text-gray-800 mb-3">Totals</h3>

    <div className="space-y-3 text-sm">
     
      {/* these 3 rows like screenshot: Rp | value */}
      <div className="flex items-center justify-between">
        <span className="text-gray-600">Total PPN</span>
        <span className="inline-flex items-center gap-3 font-semibold text-gray-900">
          <span className="w-8 text-left">Rp</span>
          <span className="w-20 text-right">{new Intl.NumberFormat("id-ID").format(Number(totals.total_ppn || 0))}</span>
        </span>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-gray-600">Total PPH</span>
        <span className="inline-flex items-center gap-3 font-semibold text-gray-900">
          <span className="w-8 text-left">Rp</span>
          <span className="w-20 text-right">{new Intl.NumberFormat("id-ID").format(Number(totals.total_pph || 0))}</span>
        </span>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-gray-600">Total Discount</span>
        <span className="inline-flex items-center gap-3 font-semibold text-gray-900">
          <span className="w-8 text-left">Rp</span>
          <span className="w-20 text-right">{new Intl.NumberFormat("id-ID").format(Number(totals.total_discount || 0))}</span>
        </span>
      </div>

      <div className="border-t border-gray-200 pt-3 flex items-center justify-between">
        <span className="text-base font-bold text-gray-900">Grand Total</span>
        <span className="text-base font-bold text-blue-600">
          {formatCurrency(totals.grand_total)}
        </span>
      </div>
    </div>
  </div>
</div>

          </div>

          {/* sticky footer */}
          <div className="sticky bottom-0 z-10 bg-gray-50/95 backdrop-blur border-t border-gray-200">
            <div className="flex justify-end gap-3 p-6">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
                disabled={submitting}
              >
                Batal
              </button>

              <button
                type="submit"
                className="px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-60"
                disabled={submitting}
                title={localFile ? "Akan muncul konfirmasi lalu upload setelah tersimpan" : "Akan muncul konfirmasi"}
              >
                {submitting ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
